import { Fragment, useState, useMemo } from "react";
import {
  CalendarPlus,
  ChevronDown,
  ChevronRight,
  EyeIcon,
  Loader2Icon,
  PackageCheck,
  Trash2,
} from "lucide-react";
import { useSWRConfig } from "swr";
import { toast } from "react-toastify";
import { Link } from "react-router";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { SortableTableHead } from "@/components/ui/sortable-table-head";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { MaskBadge } from "@/components/ui/mask-badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { formatWaferStatus, getWaferStatusColor } from "../shared/utils";
import { isPlanningKey, useMarkWaferRequestAsScheduled } from "@/hooks/useRequestPlanning";
import { useDeleteMeasurementRequest } from "@/hooks/useMeasurementRequests";
import { useSortableTable } from "@/hooks/useSortableTable";
import { MeasurementSystem } from "@/types/measurementSystem";
import Tooltip from "@/components/shared/Tooltip";
import { TruncatedCell } from "@/components/shared/TruncatedCell";
import { formatMinutesToDuration } from "@/utils/timeFormatting";
import { WaferProgressIndicator } from "@/components/shared/WaferProgressIndicator";
import { PlanningMeasurementRequest } from "@/types/planning";
import { WarningAlert } from "../shared/Alerts";
import { useIsAdmin, usePermissionContext } from "@/permissions";
import DeleteActionDialog from "../shared/DeleteActionDialog";
import { FilterableAndSortableTableHead } from "../ui/filterable-and-sortable-table-head";
import { Input } from "../ui/input";
import { abbreviateName } from "@/lib/utils";
import { TablePagination } from "../shared/TablePagination";
import { isRequestFullyBacklogged } from "@/utils/requestPlanning";

interface BacklogTableProps {
  measurementSystems: MeasurementSystem[];
  rows: PlanningMeasurementRequest[];
  waferProgressPerRequest: Map<number, { backlog: number; scheduled: number; measured: number }>;
}
export function BacklogTable({
  measurementSystems,
  rows,
  waferProgressPerRequest,
}: BacklogTableProps) {
  const { mutate } = useSWRConfig();
  const allowScheduling = useIsAdmin();
  const { hasPermission } = usePermissionContext();
  const canDeleteRequests = hasPermission("measurement_requests", "delete");
  const { trigger: deleteMeasurementRequest, isMutating: isDeletingRequest } =
    useDeleteMeasurementRequest();

  const [expandedRows, setExpandedRows] = useState<Set<number>>(() => new Set());
  const [selectedWafers, setSelectedWafers] = useState<Set<string>>(() => new Set());
  const [selectedMeasurementSystem, setSelectedMeasurementSystem] = useState<string>("");
  const [requestToDelete, setRequestToDelete] = useState<PlanningMeasurementRequest | null>(null);
  const [selectedMeasurementCapabilities, setSelectedMeasurementCapabilities] = useState<
    Map<string, string[]>
  >(() => new Map());

  const {
    sortedData: sortedRows,
    handleSort,
    renderSortIcon,
  } = useSortableTable(
    rows,
    {
      name: (row) => row.name.toLowerCase(),
      mask: (row) => row.mask.name.toLowerCase(),
      imecLotId: (row) => row.imecLotId?.toLowerCase() || "",
      measurementCapability: (row) => row.testLabel.measurementCapability?.name.toLowerCase() || "",
      requester: (row) => row.createdBy.toLowerCase(),
      requestDate: (row) => (row.createdAt ? new Date(row.createdAt).getTime() : 0),
      totalTime: (row) =>
        row.waferRequests.reduce((total, wafer) => total + wafer.estimatedTime * wafer.dieCount, 0),
    },
    { defaultColumn: "requestDate", defaultDirection: "asc" },
  );

  const { trigger: markAsScheduled, isMutating } = useMarkWaferRequestAsScheduled();

  const handleDeleteRequest = async () => {
    if (!requestToDelete || !isRequestFullyBacklogged(requestToDelete)) return;

    const request = requestToDelete;
    try {
      await deleteMeasurementRequest({ id: request.id.toString() });
      toast.success(`Successfully deleted the Measurement Request "${request.name}"!`);
      setRequestToDelete(null);
      await mutate(isPlanningKey);
      await mutate((key) => {
        if (typeof key !== "object" || key === null || !("url" in key)) return false;
        return (
          typeof key.url === "string" &&
          (key.url === "/measurement_requests" ||
            key.url.startsWith(`/measurement_requests/${request.id}`))
        );
      });
    } catch (error) {
      toast.error(`Error: ${error instanceof Error ? error.message : "Unable to delete request"}`);
    }
  };

  const [nameFilter, setNameFilter] = useState("");
  const [selectedMasks, setSelectedMasks] = useState<string[]>([]);
  const [measurementCapabilitiesFilter, setMeasurementCapabilitiesFilter] = useState<string[]>([]);
  const [showLocations, setShowLocations] = useState<string[]>([]);

  const tableHeaderFilteredData = sortedRows
    .filter((r) => {
      if (!nameFilter) return true;
      return r.name.toLowerCase().includes(nameFilter.toLowerCase());
    })
    .filter((x) => {
      if (selectedMasks.length === 0) return true;
      return selectedMasks.includes(x.mask.id);
    })
    .filter((x) => {
      if (measurementCapabilitiesFilter.length === 0) return true;
      return measurementCapabilitiesFilter.includes(x.testLabel.measurementCapability?.id ?? "");
    })
    .filter((x) => {
      if (showLocations.length === 1) {
        return showLocations[0] === "withLocations"
          ? x.physicalLocation?.trim()
          : !x.physicalLocation?.trim();
      }
      return true;
    });

  const [rowsPerPage, setRowsPerPage] = useState("25");
  const [currentPage, setCurrentPage] = useState(1);

  const totalCount = tableHeaderFilteredData.length;

  const toggleRow = (requestId: number) => {
    const newExpanded = new Set(expandedRows);

    if (newExpanded.has(requestId)) {
      newExpanded.delete(requestId);
    } else {
      newExpanded.add(requestId);
    }

    setExpandedRows(newExpanded);
  };

  const handleWaferSelection = (waferId: string, checked: boolean) => {
    const newSelected = new Set(selectedWafers);

    if (checked) {
      newSelected.add(waferId);
    } else {
      newSelected.delete(waferId);
    }

    setSelectedWafers(newSelected);
  };

  const addMeasurementCapabilityToMap = (
    measurementCapabilityId: string,
    waferId: string,
    checked: boolean,
  ) => {
    const keyData = selectedMeasurementCapabilities.get(measurementCapabilityId);

    if (keyData) {
      if (checked) {
        const uniqueWaferIds = new Set([...keyData, waferId]);
        setSelectedMeasurementCapabilities(
          selectedMeasurementCapabilities.set(measurementCapabilityId, [...uniqueWaferIds]),
        );
      } else {
        const updatedData = [...keyData].filter((x) => x !== waferId);
        if (updatedData.length === 0) {
          selectedMeasurementCapabilities.delete(measurementCapabilityId);
          setSelectedMeasurementCapabilities(selectedMeasurementCapabilities);
        } else {
          setSelectedMeasurementCapabilities(
            selectedMeasurementCapabilities.set(
              measurementCapabilityId,
              [...keyData].filter((x) => x !== waferId),
            ),
          );
        }
      }
      return;
    } else {
      if (checked) {
        setSelectedMeasurementCapabilities(
          selectedMeasurementCapabilities.set(measurementCapabilityId, [waferId]),
        );
      }
    }
  };

  const handleRequestSelection = (req: PlanningMeasurementRequest, checked: boolean) => {
    const request = rows.find((r) => r.id === req.id);
    if (!request) return;

    const newSelected = new Set(selectedWafers);

    request.waferRequests.forEach((wafer) => {
      const waferKey = `${request.id}-${wafer._id}`;
      if (checked) {
        newSelected.add(waferKey);
      } else {
        newSelected.delete(waferKey);
      }
      const capabilityId = req.testLabel.measurementCapability?.id;
      if (capabilityId) {
        addMeasurementCapabilityToMap(capabilityId, waferKey, checked);
      }
    });

    setSelectedWafers(newSelected);
  };

  const handleMarkingAsPlanned = async () => {
    try {
      if (selectedWafers.size <= 0 || !selectedMeasurementSystem) {
        return;
      }

      const wafers = Array.from(selectedWafers);

      const requestsToPlan = wafers.map((key) => {
        const [reqId, waferId] = key.split("-");
        return {
          measurement_request_id: Number(reqId),
          measurement_system_id: Number(selectedMeasurementSystem),
          wafer_id: Number(waferId),
        };
      });

      const systemName = measurementSystems.find(
        (ms) => ms.id === Number(selectedMeasurementSystem),
      )!.name;

      await markAsScheduled(
        { requests_to_plan: requestsToPlan },
        {
          onError: (err) => toast.error(`Error: ${err.message}`),
          onSuccess: () => {
            toast.success(`Successfully assigned the wafer(s) to ${systemName}!`);
            mutate(isPlanningKey);
            setSelectedWafers(new Set());
            setSelectedMeasurementSystem("");
          },
        },
      );
    } catch (error) {
      console.error(error);
    }
  };

  const isRequestSelected = (requestId: number) => {
    const request = rows.find((r) => r.id === requestId);
    if (!request) return false;

    return (
      request.waferRequests.length > 0 &&
      request.waferRequests.every((wafer) => selectedWafers.has(`${requestId}-${wafer._id}`))
    );
  };

  const pageSize = Number(rowsPerPage);
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const page = Math.min(currentPage, totalPages);
  const paginatedRows = tableHeaderFilteredData.slice((page - 1) * pageSize, page * pageSize);

  if (currentPage !== page) {
    setCurrentPage(page);
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const measurementSystemCapabilityKeys = [...selectedMeasurementCapabilities.keys()];

  const availableMeasSystemsBasedOnSelectedWafers = useMemo(() => {
    return measurementSystems.filter((system) =>
      measurementSystemCapabilityKeys.every((key) =>
        system.capabilities.some((measCap) => measCap.id === key),
      ),
    );
  }, [measurementSystemCapabilityKeys, measurementSystems]);

  const toggleMaskSelection = (value: string) => {
    if (selectedMasks.includes(value)) {
      setSelectedMasks(selectedMasks.filter((s) => s !== value));
    } else {
      setSelectedMasks([...selectedMasks, value]);
    }
  };

  const toggleMeasurementCapabilitySelection = (value: string) => {
    if (measurementCapabilitiesFilter.includes(value)) {
      setMeasurementCapabilitiesFilter(measurementCapabilitiesFilter.filter((s) => s !== value));
    } else {
      setMeasurementCapabilitiesFilter([...measurementCapabilitiesFilter, value]);
    }
  };

  const toggleShowLocationSelection = (value: string) => {
    if (showLocations.includes(value)) {
      setShowLocations(showLocations.filter((s) => s !== value));
    } else {
      setShowLocations([...showLocations, value]);
    }
  };

  const uniqueMasks = sortedRows.reduce<{ id: string; name: string }[]>((acc, cur) => {
    const exists = acc.find((x) => x.id === cur.mask.id);
    if (exists) return acc;

    return [...acc, { id: cur.mask.id, name: cur.mask.name }];
  }, []);

  const uniqueMeasurementCapabilities = sortedRows.reduce<{ id: string; name: string }[]>(
    (acc, cur) => {
      const capability = cur.testLabel.measurementCapability;
      if (!capability) return acc;

      const exists = acc.find((x) => x.id === capability.id);
      if (exists) return acc;

      return [
        ...acc,
        {
          id: capability.id,
          name: capability.name,
        },
      ];
    },
    [],
  );

  const locationOptions = [
    {
      id: "withLocations",
      name: "Has physical location",
    },
    {
      id: "withoutLocations",
      name: "No physical location",
    },
  ];

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Backlog</CardTitle>
          <div className="flex gap-2">
            {availableMeasSystemsBasedOnSelectedWafers.length === 0 && selectedWafers.size > 0 ? (
              <WarningAlert>No measurement system supports your planning selection.</WarningAlert>
            ) : (
              <>
                <Select
                  onValueChange={(val) => setSelectedMeasurementSystem(val)}
                  disabled={selectedWafers.size === 0 || isMutating}
                  value={selectedMeasurementSystem}
                >
                  <SelectTrigger className="w-62.5">
                    <SelectValue placeholder="Select a measurement system" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableMeasSystemsBasedOnSelectedWafers.map((system) => (
                      <SelectItem key={system.id} value={system.id.toString()}>
                        {`Schedule ${selectedWafers.size > 0 ? selectedWafers.size : ""} wafer(s) on ${system.name}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  disabled={isMutating || !selectedMeasurementSystem || selectedWafers.size === 0}
                  onClick={handleMarkingAsPlanned}
                >
                  {isMutating ? (
                    <Loader2Icon className="animate-spin" />
                  ) : (
                    <CalendarPlus className="h-4 w-4" />
                  )}
                  Confirm scheduling
                </Button>
              </>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/50 h-8">
              <TableHead className="w-px h-8"></TableHead>
              <TableHead className="w-12 h-8" />
              <FilterableAndSortableTableHead
                active={nameFilter.length > 0}
                column="name"
                label="Name"
                onSort={handleSort}
                renderSortIcon={renderSortIcon}
                content={
                  <div className="w-64 space-y-3">
                    <Input
                      value={nameFilter}
                      onChange={(e) => setNameFilter(e.target.value.trim())}
                      placeholder="Search by name..."
                      aria-label="Filter by name"
                    />
                    <div className="flex justify-end gap-2">
                      <Button
                        className="w-full"
                        variant="secondary"
                        onClick={() => {
                          setNameFilter("");
                        }}
                      >
                        Clear filter
                      </Button>
                    </div>
                  </div>
                }
              />
              <SortableTableHead
                column="imecLotId"
                label="Lot Id"
                onSort={handleSort}
                renderSortIcon={renderSortIcon}
              />
              <FilterableAndSortableTableHead
                active={selectedMasks.length > 0}
                column="mask"
                label="Mask"
                onSort={handleSort}
                renderSortIcon={renderSortIcon}
                content={
                  <div className="w-64 space-y-3">
                    <div className="space-y-2 max-h-48 overflow-auto px-1">
                      {uniqueMasks.map((opt) => (
                        <label key={opt.id} className="flex items-center gap-2 text-sm">
                          <Checkbox
                            checked={selectedMasks.includes(opt.id)}
                            onCheckedChange={() => toggleMaskSelection(opt.id)}
                          />
                          <span className="capitalize">{opt.name}</span>
                        </label>
                      ))}
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button
                        className="w-full"
                        variant="secondary"
                        onClick={() => {
                          setSelectedMasks([]);
                        }}
                      >
                        Clear filter
                      </Button>
                    </div>
                  </div>
                }
              />
              <FilterableAndSortableTableHead
                active={showLocations.length > 0}
                column="location"
                label="Location"
                content={
                  <div className="w-64 space-y-3">
                    <div className="space-y-2 max-h-48 overflow-auto px-1">
                      {locationOptions.map((opt) => (
                        <label key={opt.id} className="flex items-center gap-2 text-sm">
                          <Checkbox
                            checked={showLocations.includes(opt.id)}
                            onCheckedChange={() => toggleShowLocationSelection(opt.id)}
                          />
                          <span>{opt.name}</span>
                        </label>
                      ))}
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button
                        className="w-full"
                        variant="secondary"
                        onClick={() => {
                          setShowLocations([]);
                        }}
                      >
                        Clear filter
                      </Button>
                    </div>
                  </div>
                }
              />
              <FilterableAndSortableTableHead
                active={measurementCapabilitiesFilter.length > 0}
                column="measurementCapability"
                label="Measurement Cap."
                onSort={handleSort}
                renderSortIcon={renderSortIcon}
                content={
                  <div className="w-64 space-y-3">
                    <div className="space-y-2 max-h-48 overflow-auto px-1">
                      {uniqueMeasurementCapabilities.map((opt) => (
                        <label key={opt.id} className="flex items-center gap-2 text-sm">
                          <Checkbox
                            checked={measurementCapabilitiesFilter.includes(opt.id)}
                            onCheckedChange={() => toggleMeasurementCapabilitySelection(opt.id)}
                          />
                          <span className="capitalize">{opt.name}</span>
                        </label>
                      ))}
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button
                        className="w-full"
                        variant="secondary"
                        onClick={() => {
                          setMeasurementCapabilitiesFilter([]);
                        }}
                      >
                        Clear filter
                      </Button>
                    </div>
                  </div>
                }
              />
              <SortableTableHead
                column="requester"
                label="Requester"
                onSort={handleSort}
                renderSortIcon={renderSortIcon}
              />
              <SortableTableHead
                column="requestDate"
                label="Request Date"
                onSort={handleSort}
                renderSortIcon={renderSortIcon}
              />
              <SortableTableHead
                column="totalTime"
                label="Total time"
                onSort={handleSort}
                renderSortIcon={renderSortIcon}
              />
              <TableHead className="h-8">Wafers Progress</TableHead>
              <TableHead className="w-12 h-8" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {tableHeaderFilteredData.length === 0 ? (
              <TableRow>
                <TableCell className="h-10" />
                <TableCell className="h-10 py-1" />
                <TableCell className="h-10 py-1" />
                <TableCell className="h-10 py-1" />
                <TableCell className="h-10 py-1" />
                <TableCell className="h-10 py-1">
                  No requests found matching your filters.
                </TableCell>
                <TableCell className="h-10 py-1" />
                <TableCell className="h-10 py-1" />
              </TableRow>
            ) : null}
            {paginatedRows.map((request) => (
              <Fragment key={request.id}>
                <TableRow
                  className="hover:bg-muted/50 h-10 cursor-pointer"
                  onClick={() => toggleRow(request.id)}
                >
                  <TableCell className="h-10 w-px py-1 pr-2" onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={isRequestSelected(request.id)}
                      disabled={!allowScheduling}
                      onCheckedChange={(checked) =>
                        handleRequestSelection(request, checked as boolean)
                      }
                    />
                  </TableCell>
                  <TableCell className="h-10 py-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleRow(request.id);
                      }}
                    >
                      {expandedRows.has(request.id) ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                    </Button>
                  </TableCell>
                  <TableCell className="font-medium h-10 py-1 text-sm max-w-68">
                    <TruncatedCell text={request.name} />
                  </TableCell>
                  <TableCell className="h-10 py-1 text-sm">{request.imecLotId || "/"}</TableCell>
                  <TableCell className="h-10 py-1 text-sm" onClick={(e) => e.stopPropagation()}>
                    <MaskBadge maskName={request.mask.name} />
                  </TableCell>
                  <TableCell className="h-10 py-1 text-sm" onClick={(e) => e.stopPropagation()}>
                    {request.physicalLocation?.trim() ? (
                      <Tooltip
                        asChild
                        trigger={<PackageCheck />}
                        content={request.physicalLocation.trim()}
                      />
                    ) : (
                      "-"
                    )}
                  </TableCell>
                  <TableCell
                    className="h-10 py-1 text-sm max-w-32 truncate"
                    title={request.testLabel.measurementCapability?.name}
                  >
                    {request.testLabel.measurementCapability?.name}
                  </TableCell>
                  <TableCell className="h-10 py-1 text-sm max-w-20">
                    <TruncatedCell text={abbreviateName(request.createdBy)} />
                  </TableCell>
                  <TableCell className="h-10 py-1 text-sm">
                    {request.createdAt ? new Date(request.createdAt).toLocaleDateString() : "---"}
                  </TableCell>
                  <TableCell className="h-10 py-1 text-sm">
                    {(() => {
                      // Calculate total time for all wafers in this measurement request
                      const totalTime = request.waferRequests.reduce((labelTotal, wafer) => {
                        return labelTotal + wafer.estimatedTime * wafer.dieCount;
                      }, 0);
                      return totalTime > 0 ? formatMinutesToDuration(totalTime) : "---";
                    })()}
                  </TableCell>
                  <TableCell className="h-10 py-1 text-sm">
                    {(() => {
                      const progress = waferProgressPerRequest.get(request.id);
                      return progress ? (
                        <WaferProgressIndicator
                          backlog={progress.backlog}
                          scheduled={progress.scheduled}
                          measured={progress.measured}
                        />
                      ) : (
                        "---"
                      );
                    })()}
                  </TableCell>
                  <TableCell className="h-10 py-1" onClick={(e) => e.stopPropagation()}>
                    <Tooltip
                      asChild
                      trigger={
                        <Link to={`/measurement-requests/${request.id}`}>
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                            <EyeIcon className="h-4 w-4" />
                          </Button>
                        </Link>
                      }
                      content="Go to details page"
                    />
                    {canDeleteRequests ? (
                      <Tooltip
                        asChild
                        trigger={
                          <Button
                            aria-label={`Delete measurement request ${request.name}`}
                            className="h-6 w-6 p-0"
                            disabled={!isRequestFullyBacklogged(request)}
                            onClick={() => setRequestToDelete(request)}
                            size="sm"
                            variant="ghost"
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        }
                        content="Delete measurement request"
                      />
                    ) : null}
                  </TableCell>
                </TableRow>
                {expandedRows.has(request.id) && request.waferRequests.length > 0 && (
                  <TableRow key={`${request.id}-expanded`}>
                    <TableCell colSpan={12} className="p-0">
                      <div className="bg-blue-50 p-3">
                        <Table>
                          <TableHeader>
                            <TableRow className="h-8">
                              <TableHead className="w-12 h-8" />
                              <TableHead className="h-8">Wafer ID</TableHead>
                              <TableHead className="h-8">Status</TableHead>
                              <TableHead className="h-8">Test Label</TableHead>
                              <TableHead className="h-8">ChipPos</TableHead>
                              <TableHead className="h-8">Dies</TableHead>
                              <TableHead className="h-8">Time per die</TableHead>
                              <TableHead className="h-8">Total time</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {request.waferRequests.map((wafer) => {
                              const waferKey = `${request.id}-${wafer._id}`;

                              return (
                                <TableRow key={wafer.id} className="h-8">
                                  <TableCell className="h-8 py-1">
                                    <Checkbox
                                      checked={selectedWafers.has(waferKey)}
                                      disabled={!allowScheduling}
                                      onCheckedChange={(checked) => {
                                        handleWaferSelection(waferKey, checked as boolean);
                                        addMeasurementCapabilityToMap(
                                          request.testLabel.measurementCapability?.id ?? "",
                                          waferKey,
                                          checked as boolean,
                                        );
                                      }}
                                    />
                                  </TableCell>
                                  <TableCell className="font-medium h-8 py-1 text-sm">
                                    {wafer.name}
                                  </TableCell>
                                  <TableCell className="h-8 py-1">
                                    <Badge
                                      className={getWaferStatusColor(wafer.status)}
                                      variant="secondary"
                                    >
                                      {formatWaferStatus(wafer.status)}
                                    </Badge>
                                  </TableCell>
                                  <TableCell className="h-8 py-1 text-sm">
                                    <Link
                                      to={`/test-labels/${request.testLabel.id}`}
                                      className="text-blue-600 hover:text-blue-800"
                                    >
                                      {request.testLabel.name}
                                    </Link>
                                  </TableCell>
                                  <TableCell className="h-8 py-1">{wafer.chipPosName}</TableCell>
                                  <TableCell className="h-8 py-1 text-sm">
                                    {wafer.dieCount}
                                  </TableCell>
                                  <TableCell className="h-8 py-1 text-sm">
                                    {wafer.estimatedTime > 0 ? `${wafer.estimatedTime}min` : "---"}
                                  </TableCell>
                                  {wafer.estimatedTime > 0 && wafer.dieCount > 0 ? (
                                    <TableCell className="h-8 py-1 text-sm">
                                      {formatMinutesToDuration(
                                        wafer.dieCount * wafer.estimatedTime,
                                      )}
                                    </TableCell>
                                  ) : (
                                    <TableCell className="h-8 py-1 text-sm">---</TableCell>
                                  )}
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </Fragment>
            ))}
          </TableBody>
        </Table>

        <TablePagination
          totalRows={totalCount}
          currentPage={page}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
          onPageSizeChange={(value) => {
            setRowsPerPage(String(value));
            setCurrentPage(1);
          }}
        />
        <DeleteActionDialog
          action={handleDeleteRequest}
          description={
            requestToDelete
              ? `Are you sure you want to delete Measurement Request: "${requestToDelete.name}"?`
              : "Are you sure you want to delete this Measurement Request?"
          }
          isExecutingAction={isDeletingRequest}
          open={requestToDelete !== null}
          onOpenChange={(open) => {
            if (!open) setRequestToDelete(null);
          }}
          title="Delete Measurement Request"
        />
      </CardContent>
    </Card>
  );
}
